import {
  LitElement,
  html
} from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import ButtonClearStyle from '../Common/ButtonClearStyle.js';
import FocusStyle from '../Common/FocusStyle.js';
import NumericSliderStyle from './style.js';

/*
  This component overlays a range slider with opacity 0 over custom styled elements of the exact same dimensions.
  This allows us to use the native events of the range slider input, while being able to display custom
  styles that work across browsers.

  https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/range
  https://developer.mozilla.org/en-US/docs/Web/CSS/::-webkit-slider-thumb
*/

export class LdsNumericSlider extends LitElement {
  static get styles() {
    return [ButtonClearStyle, FocusStyle, NumericSliderStyle];
  }

  static properties = {
    isFocused: { state: true },
    isMoving: { state: true },
    max: { type: Number },
    maxAriaLabel: { type: String },
    maxLabel: { type: String },
    min: { type: Number },
    minAriaLabel: { type: String },
    minLabel: { type: String },
    showMinMax: { type: Boolean },
    showSteps: { type: Boolean },
    step: { type: Number },
    tiemoutValue: { state: true },
    value: { type: Number },
    valueDisplayType: { type: String },
  };

  constructor() {
    super();
    this.isFocused = false;
    this.isMoving = false;
    this.max = 100;
    this.maxAriaLabel = 'Maximum value';
    this.maxLabel = this.max;
    this.min = 0;
    this.minAriaLabel = 'Minimum value';
    this.minLabel = this.min;
    this.showMinMax = false;
    this.showSteps = false;
    this.step = 1;
    this.tiemoutValue = null;
    this.value = 0;
    this.valueDisplayType = 'never';
  }

  // set initial thumb position based on value when first loaded
  firstUpdated() {
    const range = this.shadowRoot.querySelector('#rangeSlider');
    this.updateThumb(range);
    this.setStepPosition();
  }

  updated(changedProperties) {
    if (changedProperties.step) this.setStepPosition();
  }

  // slider dropped and no longer moving
  handleChange(e) {
    const range = e.target;
    this.updateThumb(range);
    clearTimeout(this.timeoutValue);
    this.timeoutValue = setTimeout(() => {
      this.isMoving = false;
    }, 500);
  }

  // Slider moving but not dropped
  handleInput(e) {
    this.isMoving = true;
    const range = e.target;
    this.updateThumb(range);
  }

  updateThumb(range) {
    if (!range) return;

    const slider = range.nextElementSibling;
    const thumb = slider.firstElementChild;

    /* ratio is percentage of the full width we need to be at.
        for example min 0 max 100 ratio would be 10 percent when value is 10, 20 percent at 20 value etc...
        if min and max are not 0 or nice round numbers we need to calculate the new ratio
    */
    const distanceRatio = (range.value - range.min) / (range.max - range.min);
    let offset = (thumb.offsetWidth / slider.offsetWidth) * distanceRatio;
    const position = (distanceRatio - offset) * 100;

    thumb.style.insetInlineStart = position + '%';

    if (typeof thumb.style.insetInlineStart === 'undefined') {
      thumb.style.left = position + '%';
    }

    // adjust step bar color. bar to the left of the selected value will be blue
    const stepBar = this.shadowRoot.querySelector('.step-bar');
    stepBar.style.width = `${position}%`;

    // adjut color of steps if needed. any steps with a value lower than the current value get changed to .active
    const steps = this.shadowRoot.querySelectorAll('.step');
    steps.forEach((step) => {
      const stepValue = step.getAttribute('step-value');
      if (stepValue && parseInt(stepValue, 10) < range.value) {
        step.classList.add('active');
      } else {
        step.classList.remove('active');
      }
    });

    this.value = range.value;
  }

  handleFocus() {
    this.isFocused = true;
  }

  handleBlur() {
    this.isFocused = false;
  }

  /*
    add .step elements into the bar. we will add as many steps as possible based on this.step
    min and max steps are not created
  */
  htmlSteps() {
    if (!this.step || this.step < 2) return html``;

    const steps = [];
    const totalSteps = parseInt(this.max - this.min, 10) / parseInt(this.step, 10);
    for (let i = 1; i < totalSteps; i += 1) {
      const step = html`<div class="step"></div>`;
      steps.push(step);
    }
    return steps;
  }

  setStepPosition() {
    if (!this.step || this.step < 2) return;
    /*
      this function is almost identical to setting the thumb position, but we need to adjust for the step indicators
      so the indicators appear in the middle of the step.

      we do not have indicators for min and max values.
    */

    const slider = this.shadowRoot.querySelector('.lds-range-slider');
    const steps = this.shadowRoot.querySelectorAll('.step');
    const thumb = this.shadowRoot.querySelector('.lds-slider-thumb');
    steps.forEach((step, idx) => {
      const value = ((idx + 1) * this.step) + this.min;

      /* ratio is percentage of the full width we need to be at.
         for example min 0 max 100 ratio would be 10 percent when value is 10, 20 percent at 20 value etc...
         if min and max are not 0 or nice round numbers we need to calculate the new ratio
      */
      const ratio = (value - this.min) / (this.max - this.min);
      let offset = (thumb.offsetWidth / slider.offsetWidth) * ratio;
      const position = (ratio - offset) * 100;

      /*
        The left side of the thumb is where the actual value is, but we want the step indicator to be in the middle of the thumb
        so we add thumb width - half of the step width
      */
      step.style = `left: calc(${position}% + 10px)`; // eslint-disable-line
      step.setAttribute('step-value', value);
    });
  }

  render() {
    return html`
      <div
        class="lds-numeric-slider-container ${classMap({ 'show-min-max': this.showMinMax })}"
      >
        <span
          id="minLabel"
          class="min-label"
          aria-label="${this.minAriaLabel}"
        >
          ${this.minLabel}
        </span>
        <span
          class="lds-range-slider"
        >
          <input
            id="rangeSlider"
            type="range"
            aria-labelledby="minLabel maxLabel"
            min="${this.min}"
            max="${this.max}"
            step="${this.step}"
            value="${this.value}"
            @input=${this.handleInput}
            @change=${this.handleChange}
            @focus=${this.handleFocus}
            @blur=${this.handleBlur}
          />
          <span class="lds-slider" aria-hidden="true">
            <span
              class="lds-slider-thumb ${classMap({ focused: this.isFocused, moving: this.isMoving })}"
            >
              <span class="value-display ${this.valueDisplayType}">${this.value}</span>
            </span>
            <div class="steps ${classMap({ show: this.showSteps })}">
              <div class="step-bar"></div>
              ${this.htmlSteps()}
            </div>
          </span>
        </span>
        <span
          id="maxLabel"
          aria-label="${this.maxAriaLabel}"
          class="max-label"
        >
          ${this.maxLabel}
        </span>
      </div>
    `;
  }
}

export function defineComponent() {
  if (!window.customElements.get('lds-numeric-slider')) window.customElements.define('lds-numeric-slider', LdsNumericSlider);
}

export default { LdsNumericSlider, defineComponent };
